// src/modules/Investors/components/InvestorProtectedRoute.js
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../config/supabase';
import { FaSpinner, FaLock } from 'react-icons/fa';

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;

  svg {
    font-size: 32px;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  p {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const AccessDeniedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  padding: 20px;
  text-align: center;

  .icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(239, 68, 68, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;

    svg {
      font-size: 32px;
      color: #ef4444;
    }
  }

  h1 {
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    max-width: 400px;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  button {
    padding: 12px 24px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: white;
    font-size: 14px;
    cursor: pointer;
    transition: all 200ms;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const InvestorProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [investorData, setInvestorData] = useState(null);
  const location = useLocation();

  useEffect(() => {
    checkInvestorAccess();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          await verifyInvestor(session.user.email);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setIsAuthorized(false);
          setInvestorData(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const checkInvestorAccess = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        setUser(session.user);
        await verifyInvestor(session.user.email);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking investor access:', error);
      setLoading(false);
    }
  };

  const verifyInvestor = async (email) => {
    try {
      const { data, error } = await supabase
        .from('investors')
        .select('*')
        .eq('email', email.toLowerCase())
        .eq('is_active', true)
        .single();

      if (error) {
        // Se a tabela não existir ou outro erro, não autoriza mas não trava
        console.warn('Erro ao verificar investidor (tabela pode não existir):', error.message);
        setIsAuthorized(false);
        setInvestorData(null);
        setLoading(false);
        return;
      }

      if (!data) {
        setIsAuthorized(false);
        setInvestorData(null);
      } else {
        setIsAuthorized(true);
        setInvestorData(data);

        // Update last login (ignorar erros)
        try {
          await supabase
            .from('investors')
            .update({ last_login: new Date().toISOString() })
            .eq('id', data.id);

          // Log access (ignorar erros)
          await supabase
            .from('investor_access_logs')
            .insert({
              investor_email: email,
              action: 'login'
            });
        } catch (logError) {
          console.warn('Erro ao registrar acesso:', logError);
        }
      }
    } catch (error) {
      console.error('Error verifying investor:', error);
      setIsAuthorized(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/investidores';
  };

  if (loading) {
    return (
      <LoadingContainer>
        <FaSpinner />
        <p>Verificando acesso...</p>
      </LoadingContainer>
    );
  }

  // Not logged in - redirect to login
  if (!user) {
    return <Navigate to="/investidores/login" state={{ from: location }} replace />;
  }

  // Logged in but not authorized
  if (!isAuthorized) {
    return (
      <AccessDeniedContainer>
        <div className="icon">
          <FaLock />
        </div>
        <h1>Acesso Restrito</h1>
        <p>
          Sua conta ({user.email}) não está autorizada a acessar o portal de investidores.
          Entre em contato com a administração para solicitar acesso.
        </p>
        <button onClick={handleLogout}>
          Sair e tentar outra conta
        </button>
      </AccessDeniedContainer>
    );
  }

  // Authorized - render children with investor context
  return React.cloneElement(children, { investorData });
};

export default InvestorProtectedRoute;
